import React, { useState, useEffect } from 'react';
import { addDoc, collection, getDocs, deleteDoc, doc, setDoc } from 'firebase/firestore';
import { db } from './firebase';
import TutorLayout from './TutorLayout';
import './AssignmentBuilder.css';
import { MathfieldElement } from 'mathlive';
import 'mathlive';
import { BlockMath, InlineMath } from 'react-katex';
import 'katex/dist/katex.min.css';

if (!customElements.get('math-field')) {
  customElements.define('math-field', MathfieldElement);
}

const WordedQuestionBuilder = () => {
const renderMixedLatex = (input) => {
  if (!input) return null;

  // Step 1: convert \text{...} to plain text
  let normalized = input.replace(/\\text\{([^}]*)\}/g, (_, txt) => txt);

  // Step 2: wrap all LaTeX-looking commands (\frac, \sqrt, \left, etc.)
  // that are not already inside $...$ with $...$
  normalized = normalized.replace(/(\\[a-zA-Z]+[^\s]*)/g, (match) => {
    // If it's already inside $, leave it
    if (match.startsWith("$") && match.endsWith("$")) return match;
    return `$${match}$`;
  });

  // Step 3: split only on $...$ and $$...$$
  const re = /(\$\$[\s\S]+?\$\$|\$[\s\S]+?\$)/g;
  const out = [];
  let last = 0;

  normalized.replace(re, (m, _, offset) => {
    if (offset > last) out.push({ t: "text", v: normalized.slice(last, offset) });
    if (m.startsWith("$$")) {
      out.push({ t: "block", v: m.slice(2, -2) });
    } else {
      out.push({ t: "inline", v: m.slice(1, -1) });
    }
    last = offset + m.length;
    return m;
  });

  if (last < normalized.length) out.push({ t: "text", v: normalized.slice(last) });

  return out.map((p, i) =>
    p.t === "text"  ? <span key={i}>{p.v}</span> :
    p.t === "block" ? <div key={i} style={{ margin: "0.5rem 0" }}>
                        <BlockMath math={p.v} />
                      </div> :
                      <InlineMath key={i} math={p.v} />
  );
};




  const [subject, setSubject] = useState(localStorage.getItem('qb_subject') || '');
  const [topic, setTopic] = useState(localStorage.getItem('qb_topic') || '');
  const [subtopic, setSubtopic] = useState(localStorage.getItem('qb_subtopic') || '');
  const [clip, setClip] = useState(localStorage.getItem('qb_clip') || '');
  const [questionParagraphs, setQuestionParagraphs] = useState([{ text: '', image: '' }]);
  const [answers, setAnswers] = useState(['']);
  const [choices, setChoices] = useState([{ text: '', isCorrect: false }]);
  const [answerPrefix, setAnswerPrefix] = useState('');
  const [answerSuffix, setAnswerSuffix] = useState('');
  const [calculator, setCalculator] = useState('yes');
  const [difficulty, setDifficulty] = useState('');
  const [savedQuestions, setSavedQuestions] = useState([]);
  const [filter, setFilter] = useState({ subject: '', topic: '', subtopic: '' });
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const getFilteredTopics = () => {
    return [
      ...new Set(
        savedQuestions
          .filter(q => !filter.subject || q.subject === filter.subject)
          .map(q => q.topic)
          .filter(Boolean)
      )
    ];
  };
  
  const getFilteredSubtopics = () => {
    return [
      ...new Set(
        savedQuestions
          .filter(q =>
            (!filter.subject || q.subject === filter.subject) &&
            (!filter.topic || q.topic === filter.topic)
          )
          .map(q => q.subtopic)
          .filter(Boolean)
      )
    ];
  };
  
  const getFilteredClips = () => {
    return [
      ...new Set(
        savedQuestions
          .filter(q =>
            (!filter.subject || q.subject === filter.subject) &&
            (!filter.topic || q.topic === filter.topic) &&
            (!filter.subtopic || q.subtopic === filter.subtopic)
          )
          .map(q => q.clip)
          .filter(Boolean)
      )
    ];
  };
  useEffect(() => { fetchQuestions(); }, []);

  const fetchQuestions = async () => {
    const querySnapshot = await getDocs(collection(db, 'questions'));
    const questionsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setSavedQuestions(questionsData);
  };

  useEffect(() => localStorage.setItem('qb_subject', subject), [subject]);
  useEffect(() => localStorage.setItem('qb_topic', topic), [topic]);
  useEffect(() => localStorage.setItem('qb_subtopic', subtopic), [subtopic]);
  useEffect(() => localStorage.setItem('qb_clip', clip), [clip]);

  const saveQuestion = async () => {
    const validAnswers = answers.map(ans => ans.trim()).filter(Boolean);
    const validParagraphs = questionParagraphs.filter(p => p.text.trim() !== '' || p.image.trim() !== '');
    const validChoices = choices.filter(c => c.text.trim() !== '');

    if (!subject || !topic || !subtopic || !clip || validParagraphs.length === 0 || validAnswers.length === 0) {
      return alert('Please fill in all required fields.');
    }

    const payload = {
      subject,
      topic,
      subtopic,
      clip,
      questionParagraphs: validParagraphs,
      answers: validAnswers,
      choices: validChoices,
      answerPrefix,
      answerSuffix,
      calculator,
      difficulty
    };

    if (editMode && editingId) {
      await setDoc(doc(db, 'questions', editingId), payload);
    } else {
      await addDoc(collection(db, 'questions'), payload);
    }

    resetForm();
    fetchQuestions();
  };

  const resetForm = () => {
    setQuestionParagraphs([{ text: '', image: '' }]);
    setAnswers(['']);
    setChoices([{ text: '', isCorrect: false }]);
    setAnswerPrefix('');
    setAnswerSuffix('');
    setEditMode(false);
    setEditingId(null);
  };

  const deleteQuestion = async (id) => {
    await deleteDoc(doc(db, 'questions', id));
    fetchQuestions();
  };

  const loadForEditing = (q) => {
    setSubject(q.subject || '');
    setTopic(q.topic || '');
    setSubtopic(q.subtopic || '');
    setClip(q.clip || '');
    setQuestionParagraphs(q.questionParagraphs || [{ text: '', image: '' }]);
    setAnswers(q.answers || ['']);
    setChoices(q.choices || [{ text: '', isCorrect: false }]);
    setAnswerPrefix(q.answerPrefix || '');
    setAnswerSuffix(q.answerSuffix || '');
    setCalculator(q.calculator || 'yes');
    setDifficulty(q.difficulty || '');
    setEditMode(true);
    setEditingId(q.id);
  };

  const updateAnswer = (index, value) => {
    const newAnswers = [...answers];
    newAnswers[index] = value;
    setAnswers(newAnswers);
  };

  const addAnswerField = () => setAnswers([...answers, '']);

  const updateChoice = (index, key, value) => {
    const newChoices = [...choices];
    newChoices[index][key] = key === 'isCorrect' ? value.target.checked : value.target.value;
    setChoices(newChoices);
  };

  const addChoiceField = () => setChoices([...choices, { text: '', isCorrect: false }]);

  const updateParagraphText = (index, value) => {
    const updated = [...questionParagraphs];
    // ensure math is wrapped
 updated[index].text = value;
    setQuestionParagraphs(updated);
  };

  const updateParagraphImage = (index, value) => {
    const updated = [...questionParagraphs];
    updated[index].image = value;
    setQuestionParagraphs(updated);
  };

  const addParagraphField = () => {
    setQuestionParagraphs([...questionParagraphs, { text: '', image: '' }]);
  };

  const getUnique = (field) => [...new Set(savedQuestions.map(q => q[field]).filter(Boolean))];

  const filteredQuestions = savedQuestions.filter(q => {
    return (
      (!filter.subject || q.subject === filter.subject) &&
      (!filter.topic || q.topic === filter.topic) &&
      (!filter.subtopic || q.subtopic === filter.subtopic) &&
      (!filter.clip || q.clip === filter.clip)
    );
  });
  

  return (
    <TutorLayout>
      <div className="builder-wrapper">
        <h1>📝 Worded Question Builder</h1>

        <div className="section">
          {/* Subject-Topic UI remains unchanged */}
          <label>Subject:</label>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} className="fancy-dropdown" placeholder="e.g. GCSE Maths" />

          <label>Topic:</label>
          <input value={topic} onChange={(e) => setTopic(e.target.value)} className="fancy-dropdown" placeholder="e.g. Algebra" />

          <label>Subtopic:</label>
          <input value={subtopic} onChange={(e) => setSubtopic(e.target.value)} className="fancy-dropdown" placeholder="e.g. Solving Equations" />

          <label>Clip:</label>
          <input value={clip} onChange={(e) => setClip(e.target.value)} className="fancy-dropdown" placeholder="e.g. Solving Linear Equations" />

          <label>Calculator Allowed?</label>
          <select value={calculator} onChange={(e) => setCalculator(e.target.value)} className="fancy-dropdown">
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>

          <label>Difficulty (1-10):</label>
          <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)} className="fancy-dropdown">
            <option value="">Select Difficulty</option>
            {[...Array(10)].map((_, i) => (
              <option key={i + 1} value={String(i + 1)}>{i + 1}</option>
            ))}
          </select>

          {/* Paragraph entry */}
          <label>Question Paragraphs:</label>
          {questionParagraphs.map((para, index) => (
            <div key={index} style={{ marginBottom: '1.5rem' }}>
              <math-field
    value={para.text}
    onInput={(e) => updateParagraphText(index, e.target.value)}
    placeholder={`Paragraph ${index + 1} — use $...$ for inline math or $$...$$ for display`}
    style={{
      width: '100%',
      padding: '0.6rem',
      fontSize: '1.05rem',
      border: '1px solid #ccc',
      borderRadius: '8px',
      marginBottom: '0.4rem'
    }}
      />
              <input
                value={para.image}
                onChange={(e) => updateParagraphImage(index, e.target.value)}
                placeholder="Optional image URL"
                className="fancy-dropdown"
                style={{ marginTop: '0.5rem' }}
              />
            </div>
          ))}
          <button onClick={addParagraphField} className="add-btn">➕ Add Paragraph</button>

          <label>Answer Prefix (optional):</label>
<math-field
  value={answerPrefix}
  onInput={(e) => setAnswerPrefix(e.target.value)}
  style={{
    marginBottom: '0.75rem',
    width: '100%',
    padding: '0.5rem',
    fontSize: '1.1rem',
    border: '1px solid #ccc',
    borderRadius: '8px'
  }}
></math-field>

<label>Answer Suffix (optional):</label>
<math-field
  value={answerSuffix}
  onInput={(e) => setAnswerSuffix(e.target.value)}
  style={{
    marginBottom: '0.75rem',
    width: '100%',
    padding: '0.5rem',
    fontSize: '1.1rem',
    border: '1px solid #ccc',
    borderRadius: '8px'
  }}
></math-field>

          <label>Acceptable Answers:</label>
          {answers.map((ans, index) => (
            <math-field
              key={index}
              value={ans}
              onInput={(e) => updateAnswer(index, e.target.value)}
              style={{
                marginBottom: '0.75rem',
                width: '100%',
                padding: '0.5rem',
                fontSize: '1.1rem',
                border: '1px solid #ccc',
                borderRadius: '8px'
              }}
            ></math-field>
          ))}
          <button onClick={addAnswerField} className="add-btn">➕ Add Another Answer</button>

          <label>Multiple Choice Options:</label>
          {choices.map((choice, index) => (
            <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
              <math-field
                value={choice.text}
                onInput={(e) => updateChoice(index, 'text', { target: { value: e.target.value } })}
                style={{
                  flex: 1,
                  marginRight: '0.75rem',
                  padding: '0.4rem',
                  fontSize: '1rem',
                  border: '1px solid #ccc',
                  borderRadius: '8px'
                }}
              ></math-field>
              <label>
                <input
                  type="radio"
                  name="correctChoice"
                  checked={choice.isCorrect}
                  onChange={() => {
                    const updated = choices.map((c, i) => ({ ...c, isCorrect: i === index }));
                    setChoices(updated);
                  }}
                />
                Correct
              </label>
            </div>
          ))}
          <button onClick={addChoiceField} className="add-btn">➕ Add Option</button>

          <button onClick={saveQuestion} className="save-btn" style={{ marginTop: '1rem' }}>
            {editMode ? '✅ Update Question' : '💾 Save Question'}
          </button>
        </div>

        <div className="section">
        <h3>🔍 Filter Questions</h3>

<select
  onChange={(e) => setFilter({ subject: e.target.value, topic: '', subtopic: '', clip: '' })}
  value={filter.subject}
  className="fancy-dropdown"
>
  <option value="">All Subjects</option>
  {getUnique('subject').map(s => <option key={s}>{s}</option>)}
</select>

<select
  onChange={(e) => setFilter({ ...filter, topic: e.target.value, subtopic: '', clip: '' })}
  value={filter.topic}
  className="fancy-dropdown"
>
  <option value="">All Topics</option>
  {getFilteredTopics().map(t => <option key={t}>{t}</option>)}
</select>

<select
  onChange={(e) => setFilter({ ...filter, subtopic: e.target.value, clip: '' })}
  value={filter.subtopic}
  className="fancy-dropdown"
>
  <option value="">All Subtopics</option>
  {getFilteredSubtopics().map(st => <option key={st}>{st}</option>)}
</select>

<select
  onChange={(e) => setFilter({ ...filter, clip: e.target.value })}
  value={filter.clip}
  className="fancy-dropdown"
>
  <option value="">All Clips</option>
  {getFilteredClips().map(c => <option key={c}>{c}</option>)}
</select>



        </div>

        <div className="section">
  <h3>📚 Saved Questions</h3>

  {!filter.clip ? (
    <p>🎯 Select a clip above to view saved questions.</p>
  ) : filteredQuestions.length === 0 ? (
    <p>No questions found for this clip.</p>
  ) : (
    filteredQuestions.map((q) => {
      const hasContent =
      (q.questionText && q.questionText.trim() !== '') ||
      (q.questionLatex && q.questionLatex.trim() !== '') ||
      (q.optionalQuestionText && q.optionalQuestionText.trim() !== '') ||
      (Array.isArray(q.questionParagraphs) && q.questionParagraphs.length > 0) ||
      (Array.isArray(q.choices) && q.choices.length > 0);
    
    
      if (!hasContent) return null; // 🔒 Skip blank questions
    
      return (
        <div key={q.id} className="question-card">
                <div className="question-actions-top">
                <button onClick={() => loadForEditing(q)} className="edit-btn">✏️</button>
  <button onClick={() => deleteQuestion(q.id)} className="delete-btn">🗑</button>
</div>

              {q.questionParagraphs?.length > 0 && (
  <div style={{ display: "flex", flexDirection: "column", gap: "1rem", marginBottom: "1rem" }}>
    {q.questionParagraphs.map((para, i) => (
      <div key={i}>
        {para.image && (
          <img
          src={para.image}
          alt={`Image ${i + 1}`}
          style={{
            width: "150px",           // ✅ Force image width
            height: "150px",          // ✅ Force image height
            objectFit: "contain",     // ✅ Keep aspect ratio clean
            display: "block",
            margin: "0 auto 1rem",
          }}
        />
        
        )}
       {para.text && (
   <div style={{ marginTop: "0.5rem", lineHeight: 1.5 }}>
     {renderMixedLatex(para.text)}
   </div>
 )}
   
 
      </div>
    ))}
  </div>
)}

            
              
<div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "1rem" }}>
  {q.questionText && (
    <div>
      <strong>Q:</strong> {q.questionText}
    </div>
  )}

  {q.questionLatex && (
    <div style={{ paddingLeft: "1rem" }}>
      <BlockMath math={q.questionLatex} />
    </div>
  )}

  {q.optionalQuestionText && (
    <div style={{ paddingLeft: "1rem", color: "#333" }}>
      {q.optionalQuestionText}
    </div>
  )}
</div>

            
              {q.choices?.length > 0 && (
                <div className="choice-grid">
                {q.choices.map((c, i) => (
                  <div key={i} className="choice-box">
                    <div>
   {renderMixedLatex(c.text)} {c.isCorrect && <strong>✅</strong>}
 </div>
                  </div>
                ))}
              </div>
              
              )}
            
              
            </div>
            
          );
        })
          )}
        </div>
      </div>
    </TutorLayout>
  );
};

export default WordedQuestionBuilder;